// src/router.tsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TestProvider } from './context/TestContext';
import { LangProvider } from './context/LangContext';
import { LangToggle } from './components/common/LangToggle';
import { CloseButton } from './components/common/CloseButton';
import { ADMIN_LOGIN_PATH } from './constants';
import LoginPage from './pages/LoginPage';
import RequisitesPage from './pages/RequisitesPage';
import PrivacyPage from './pages/PrivacyPage';
import OfferPage from './pages/OfferPage';
import TestsPage from './pages/TestsPage';
import TestEditorPage from './pages/TestEditorPage';
import TestPage from './pages/TestPage';
import UsersPage from './pages/UsersPage';
import ResultsPage from './pages/ResultsPage';
import ConclusionPage from './pages/ConclusionPage';
import ConclusionsListPage from './pages/ConclusionsListPage';
import MyResultsPage from './pages/MyResultsPage';
import PublicResultPage from './pages/PublicResultPage';
import ResultsGatePage from './pages/ResultsGatePage';
import PublicComparePage from './pages/PublicComparePage';
import CompareResultPage from './pages/CompareResultPage';
import DbTransferPage from './pages/DbTransferPage';
import MediaPage from './pages/MediaPage';

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  role?: 'admin' | 'guest' | 'user'
}> = ({ children, role }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to={ADMIN_LOGIN_PATH} replace />;
  }

  if (role && user?.role !== role) {
    return <Navigate to="/tests" replace />;
  }

  return <>{children}</>;
};

const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <LangProvider>
      <LangToggle />
      <CloseButton />
      <AuthProvider>
        <TestProvider>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/requisites" element={<RequisitesPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/offer" element={<OfferPage />} />
            {/* Скрытая страница: здесь кнопка «Вход в личный кабинет» */}
            <Route path={ADMIN_LOGIN_PATH} element={<LoginPage showAdminLogin />} />
            <Route path="/results-gates" element={<ResultsGatePage />} />
            <Route path="/result/:code" element={<PublicResultPage />} />
            <Route path="/compare-entry" element={<PublicComparePage />} />
            <Route path="/compare-result/:code" element={<CompareResultPage />} />
            <Route
              path="/tests"
              element={
                <ProtectedRoute>
                  <TestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/test-editor/:testId"
              element={
                <ProtectedRoute role="admin">
                  <TestEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/test/:testId"
              element={
                <ProtectedRoute>
                  <TestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute role="admin">
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/results"
              element={
                <ProtectedRoute role="admin">
                  <ResultsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/db-transfer"
              element={
                <ProtectedRoute role="admin">
                  <DbTransferPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/media"
              element={
                <ProtectedRoute role="admin">
                  <MediaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/conclusion/:id"
              element={
                <ProtectedRoute role="admin">
                  <ConclusionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-results"
              element={
                <ProtectedRoute>
                  <MyResultsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/conclusions"
              element={
                <ProtectedRoute role="admin">
                  <ConclusionsListPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </TestProvider>
      </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  );
};

export default AppRouter;
